/**
 * Golden Circle — frontend behaviour.
 *
 * Highlights one layer of the circle at a time (ring + matching card) and draws
 * the SVG connector between the edge of the active ring and its card. All
 * geometry is read from the live layout, so the block stays correct at any
 * stage size, after resizing and while the rings are pulsing.
 */

const ACTIVE_CLASS = 'is-active';
const HIDDEN_CLASS = 'is-hidden';
const LINE_TRANSITION = 'stroke-dashoffset .55s cubic-bezier(.25,.8,.3,1)';

// Matches the `lg` SCSS breakpoint — below it the block is a static, fully expanded list.
const DESKTOP_QUERY = '(min-width: 992px)';
const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
const supportsHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;

/**
 * Replay the draw-in animation of the connector line.
 *
 * @param {SVGPathElement} line Connector path.
 * @return {void}
 */
const animateLine = ( line ) => {

	const length = line.getTotalLength();

	if ( ! length || prefersReducedMotion ) {

		line.style.transition = 'none';
		line.style.strokeDasharray = 'none';
		line.style.strokeDashoffset = '0';
		return;

	}

	line.style.transition = 'none';
	line.style.strokeDasharray = `${ length }`;
	line.style.strokeDashoffset = `${ length }`;
	line.getBoundingClientRect();
	line.style.transition = LINE_TRANSITION;
	line.style.strokeDashoffset = '0';

};

/**
 * Find the element matching a layer key.
 *
 * @param {HTMLElement[]} elements Elements carrying a layer key.
 * @param {string}        key      Layer key.
 * @return {HTMLElement|null} Matching element.
 */
const findByKey = ( elements, key ) => elements.find( ( element ) => element.dataset.gcKey === key ) || null;

/**
 * Initialise a single Golden Circle block.
 *
 * @param {HTMLElement} block      Block root element.
 * @param {number}      blockIndex Position of the block among all Golden Circle blocks on the page.
 * @return {void}
 */
const initGoldenCircle = ( block, blockIndex ) => {

	const stage = block.querySelector( '[data-gc-stage]' );
	const cardsWrap = block.querySelector( '[data-gc-cards]' );

	if ( ! stage || ! cardsWrap ) {

		return;

	}

	const rings = Array.from( stage.querySelectorAll( '[data-gc-key]' ) );
	const cards = Array.from( cardsWrap.querySelectorAll( '[data-gc-key]' ) );

	if ( ! rings.length || ! cards.length ) {

		return;

	}

	// Falls back to a page-unique prefix so `aria-controls` never targets a duplicate id
	// when several Golden Circle blocks are rendered on the same page.
	const instanceId = block.id || `golden-circle-${ blockIndex }`;

	rings.forEach( ( ring ) => {

		const card = findByKey( cards, ring.dataset.gcKey );

		if ( ! card ) {

			return;

		}

		if ( ! card.id ) {

			card.id = `${ instanceId }-panel-${ card.dataset.gcKey }`;

		}

		ring.setAttribute( 'aria-controls', card.id );

	} );

	const triggers = rings.concat( cards );
	const connector = stage.querySelector( '[data-gc-connector]' );
	const line = connector ? connector.querySelector( '[data-gc-line]' ) : null;
	const dot = connector ? connector.querySelector( '[data-gc-dot]' ) : null;
	const initial = triggers.find( ( element ) => element.classList.contains( ACTIVE_CLASS ) ) || cards[ 0 ];
	const desktopQuery = window.matchMedia( DESKTOP_QUERY );
	const initialKey = initial.dataset.gcKey;

	let activeKey = initialKey;
	let frameId = null;
	const isOnScreen = { value: true };
	let connectorWidth = 0;
	let connectorHeight = 0;

	/**
	 * Keep the connector viewport in sync with the current stage size.
	 *
	 * @param {number} width  Stage width in pixels.
	 * @param {number} height Stage height in pixels.
	 * @return {void}
	 */
	const syncConnectorSize = ( width, height ) => {

		const nextWidth = Math.round( width );
		const nextHeight = Math.round( height );

		if ( nextWidth === connectorWidth && nextHeight === connectorHeight ) {

			return;

		}

		connectorWidth = nextWidth;
		connectorHeight = nextHeight;
		connector.setAttribute( 'width', `${ nextWidth }` );
		connector.setAttribute( 'height', `${ nextHeight }` );
		connector.setAttribute( 'viewBox', `0 0 ${ nextWidth } ${ nextHeight }` );

	};

	/**
	 * Hide the connector when there is nothing meaningful to draw.
	 *
	 * @return {void}
	 */
	const hideConnector = () => {

		connector.classList.add( HIDDEN_CLASS );
		line.setAttribute( 'd', '' );

	};

	/**
	 * Draw the connector between the active ring and the active card.
	 *
	 * @param {boolean} animate Whether to replay the draw-in animation.
	 * @return {void}
	 */
	const drawConnector = ( animate ) => {

		if ( ! connector || ! line || ! dot || ! connector.getClientRects().length ) {

			return;

		}

		const ring = findByKey( rings, activeKey );
		const card = findByKey( cards, activeKey );
		const stageRect = stage.getBoundingClientRect();

		if ( ! ring || ! card || ! stageRect.width ) {

			hideConnector();
			return;

		}

		const ringRect = ring.getBoundingClientRect();
		const cardRect = card.getBoundingClientRect();
		const centerX = stageRect.width / 2;
		const centerY = stageRect.height / 2;
		const radius = ringRect.width / 2;
		const targetX = cardRect.left - stageRect.left;
		const targetY = cardRect.top - stageRect.top + cardRect.height / 2;

		// Stacked layout — the cards sit below the stage, a horizontal line makes no sense.
		if ( targetX <= centerX + radius ) {

			hideConnector();
			return;

		}

		syncConnectorSize( stageRect.width, stageRect.height );

		// The line always starts on the ring outline, aimed at the card — when the card
		// sits above or below the ring the connector simply tilts instead of detaching.
		const deltaX = targetX - centerX;
		const deltaY = targetY - centerY;
		const distance = Math.sqrt( ( deltaX * deltaX ) + ( deltaY * deltaY ) ) || 1;
		const startX = centerX + ( ( deltaX / distance ) * radius );
		const startY = centerY + ( ( deltaY / distance ) * radius );

		line.setAttribute( 'd', `M ${ startX } ${ startY } L ${ targetX } ${ targetY }` );
		dot.setAttribute( 'cx', `${ startX }` );
		dot.setAttribute( 'cy', `${ startY }` );
		connector.classList.remove( HIDDEN_CLASS );

		if ( animate ) {

			animateLine( line );

		}

	};

	/**
	 * Reflect the active key on every ring and card.
	 *
	 * @return {void}
	 */
	const syncActiveClasses = () => {

		triggers.forEach( ( element ) => element.classList.toggle( ACTIVE_CLASS, element.dataset.gcKey === activeKey ) );

		// Below the desktop breakpoint every card stays fully expanded, so every
		// ring is reported as expanded too — only the desktop accordion is exclusive.
		rings.forEach( ( ring ) => {

			const isExpanded = ! desktopQuery.matches || ring.dataset.gcKey === activeKey;
			ring.setAttribute( 'aria-expanded', String( isExpanded ) );

		} );

	};

	/**
	 * Highlight a layer by key.
	 *
	 * @param {string} key Layer key.
	 * @return {void}
	 */
	const setActiveLayer = ( key ) => {

		if ( ! desktopQuery.matches || ! key || key === activeKey ) {

			return;

		}

		activeKey = key;
		syncActiveClasses();
		drawConnector( true );

	};

	/**
	 * Start the redraw loop that keeps the connector glued to the pulsing ring.
	 *
	 * @return {void}
	 */
	const startLoop = () => {

		if ( frameId !== null ) {

			return;

		}

		/**
		 * Redraw on every animation frame.
		 *
		 * @return {void}
		 */
		const loop = () => {

			drawConnector( false );
			frameId = window.requestAnimationFrame( loop );

		};

		loop();

	};

	/**
	 * Stop the redraw loop while the block is off-screen.
	 *
	 * @return {void}
	 */
	const stopLoop = () => {

		if ( frameId === null ) {

			return;

		}

		window.cancelAnimationFrame( frameId );
		frameId = null;

	};

	/**
	 * Enable or disable the whole interaction layer for the current viewport.
	 *
	 * @return {void}
	 */
	const applyMode = () => {

		const isInteractive = desktopQuery.matches;

		rings.forEach( ( ring ) => {

			ring.disabled = ! isInteractive;

		} );

		if ( isInteractive ) {

			if ( isOnScreen.value ) {

				startLoop();

			}

			return;

		}

		stopLoop();
		activeKey = initialKey;
		syncActiveClasses();

		if ( connector && line ) {

			hideConnector();

		}

	};

	triggers.forEach( ( element ) => {

		// Touch devices get click/tap only — emulated hover would fire on the first tap
		// and leave the layer highlighted without the user asking for it.
		if ( supportsHover ) {

			element.addEventListener( 'mouseenter', () => setActiveLayer( element.dataset.gcKey ) );

		}

		element.addEventListener( 'click', () => setActiveLayer( element.dataset.gcKey ) );
		element.addEventListener( 'focusin', () => setActiveLayer( element.dataset.gcKey ) );

	} );

	syncActiveClasses();
	desktopQuery.addEventListener( 'change', applyMode );

	if ( 'IntersectionObserver' in window ) {

		const observer = new window.IntersectionObserver( ( entries ) => {

			entries.forEach( ( entry ) => {

				isOnScreen.value = entry.isIntersecting;
				applyMode();

			} );

		}, { rootMargin: '100px' } );

		observer.observe( block );
		return;

	}

	applyMode();

};

document.querySelectorAll( '.b-golden-circle' ).forEach( ( block, blockIndex ) => initGoldenCircle( block, blockIndex ) );
